import type { ReactNode } from "react";

import {
  HoverHintDefinitionGrid,
  skuQuantityPeekRows,
  type HoverHintDefinitionRow,
} from "@/components/ui/HoverHintDefinitionGrid";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { EM_DASH } from "@/lib/utils";

import type {
  BatchPeekContext,
  CarrierPeekContext,
  DevicePeekContext,
  GosiPeekContext,
  PurchaseOrderPeekContext,
  RcaPeekContext,
  ShipmentPeekContext,
  WorkOrderInlinePeekContext,
} from "./entityPeekTypes";
import { SalesOrderPeekHoverHintBody } from "./SalesOrderPeekHoverHintBody";
import type { CalendarSalesOrderPeekContext } from "./entityPeekTypes";

function peekField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : EM_DASH;
}

function formatPeekDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function BatchPeekHoverHintBody({
  context,
  emptyMessage = "Batch details unavailable.",
}: {
  context: BatchPeekContext | undefined;
  emptyMessage?: ReactNode;
}): ReactNode {
  if (context?.loading === true) {
    return <span className="text-slate-500">Loading batch details…</span>;
  }
  if (context == null) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  const rows: HoverHintDefinitionRow[] = [];
  if (context.mfr_ship_by_date != null) {
    rows.push({ label: "Mfr ship by", value: formatPeekDate(context.mfr_ship_by_date) });
  }
  if (context.ship_to_label != null && context.ship_to_label.trim() !== "") {
    rows.push({ label: "Ship to", value: context.ship_to_label.trim() });
  }
  if (context.work_order_numbers != null && context.work_order_numbers.length > 0) {
    const workOrders = context.work_order_numbers;
    const value =
      workOrders.length <= 4
        ? workOrders.join(", ")
        : `${workOrders.slice(0, 3).join(", ")} +${workOrders.length - 3} more`;
    rows.push({ label: "Work orders", value });
  }
  if (context.line_count != null || context.serial_count != null) {
    rows.push({
      label: "Lines",
      value: (
        <span className="tabular-nums">
          {(context.line_count ?? 0).toLocaleString()}
          <span className="text-slate-600">{` · ${(context.serial_count ?? 0).toLocaleString()} devices`}</span>
        </span>
      ),
    });
  }
  const finishedSkuLines = context.finished_sku_lines ?? [];
  if (finishedSkuLines.length > 0) {
    const visibleLines = finishedSkuLines.length <= 4 ? finishedSkuLines : finishedSkuLines.slice(0, 3);
    rows.push(...skuQuantityPeekRows(visibleLines));
    if (finishedSkuLines.length > 4) {
      rows.push({
        label: "More SKUs",
        value: <span className="tabular-nums">+{finishedSkuLines.length - 3} more</span>,
      });
    }
  }
  if (context.shipment_on_hold) {
    rows.push({ label: "Hold", value: <span className="font-medium text-amber-800">Shipment on hold</span> });
  }
  if (rows.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function ShipmentPeekHoverHintBody({
  context,
  emptyMessage = "No shipment details.",
}: {
  context: ShipmentPeekContext | undefined;
  emptyMessage?: ReactNode;
}): ReactNode {
  const rows: HoverHintDefinitionRow[] = [];
  if (context?.ship_date != null) {
    rows.push({ label: "Ship date", value: formatPeekDate(context.ship_date) });
  }
  if (context?.ship_by_date != null) {
    rows.push({ label: "Ship by", value: formatPeekDate(context.ship_by_date) });
  }
  if (context?.ship_to_label != null && context.ship_to_label.trim() !== "") {
    rows.push({
      label: "Ship to",
      value: <span className="whitespace-pre-wrap">{context.ship_to_label.trim()}</span>,
    });
  }
  if (rows.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function PurchaseOrderPeekHoverHintBody({
  context,
  emptyMessage = "No purchase order context.",
}: {
  context: PurchaseOrderPeekContext | undefined;
  emptyMessage?: ReactNode;
}): ReactNode {
  if (context == null) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  const rows: HoverHintDefinitionRow[] = [];
  if (context.purchase_order_number != null && context.purchase_order_number.trim() !== "") {
    rows.push({ label: "Purchase order", value: context.purchase_order_number.trim() });
  }
  if (context.promise_date != null && context.promise_date.trim() !== "") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(context.promise_date.trim());
    const promiseLabel =
      match != null ? `${match[2]}-${match[3]}-${match[1]}` : context.promise_date.trim();
    rows.push({ label: "Promise", value: promiseLabel });
  }
  if (rows.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function GosiPeekHoverHintBody({ context }: { context: GosiPeekContext }): ReactNode {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Document", value: peekField(context.document_number) },
        {
          label: "MV document",
          value:
            context.mv_document_id != null ? (
              <span className="tabular-nums">{context.mv_document_id}</span>
            ) : (
              EM_DASH
            ),
        },
        { label: "Status", value: peekField(context.status) },
        { label: "Terms", value: peekField(context.terms) },
        { label: "Created", value: peekField(context.date_created) },
        { label: "Sales order", value: peekField(context.sales_order_number) },
        { label: "Customer PO", value: peekField(context.customer_purchase_order_number) },
        { label: "Client", value: peekField(context.client_name) },
      ]}
    />
  );
}

export function DevicePeekHoverHintBody({ context }: { context: DevicePeekContext }): ReactNode {
  const locationId = context.pcbaLocationId;
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Serial", value: <span className="font-mono">{context.serial}</span> },
        { label: "Model", value: peekField(context.model) },
        { label: "Firmware", value: peekField(context.firmwareVersion) },
        {
          label: "PCBA manufacturer",
          value:
            locationId != null && locationId > 0 ? <ManufacturingLocationChip locationId={locationId} /> : EM_DASH,
        },
        { label: "Communicating", value: context.communicating ? "Yes" : EM_DASH },
      ]}
    />
  );
}

export function RcaPeekHoverHintBody({ context }: { context: RcaPeekContext }): ReactNode {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Status", value: peekField(context.status) },
        {
          label: "Opened",
          value: context.openedAutomatically ? "Automatically" : formatPeekDate(context.openedAt),
        },
        {
          label: "Opened by",
          value: context.openedAutomatically ? EM_DASH : peekField(context.openedByDisplay),
        },
        { label: "RMA", value: peekField(context.rmaNumber) },
        { label: "Summary", value: peekField(context.summary) },
      ]}
    />
  );
}

export function CarrierPeekHoverHintBody({ context }: { context: CarrierPeekContext }): ReactNode {
  const rows: HoverHintDefinitionRow[] = [{ label: "Carrier", value: context.carrier }];
  const trackingNumbers = (context.tracking_numbers ?? []).map((value) => value.trim()).filter((value) => value !== "");
  if (trackingNumbers.length > 0) {
    rows.push({
      label: "Tracking",
      value: (
        <span className="block font-mono text-[11px] leading-snug">
          {trackingNumbers.map((trackingNumber) => (
            <span key={trackingNumber} className="block">
              {trackingNumber}
            </span>
          ))}
        </span>
      ),
    });
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function WorkOrderInlinePeekHoverHintBody({
  context,
}: {
  context: WorkOrderInlinePeekContext;
}): ReactNode {
  const rows: HoverHintDefinitionRow[] = [];
  if (context.sku != null && context.sku.trim() !== "") {
    rows.push({ label: "SKU", value: context.sku.trim() });
  }
  if (context.location_label != null && context.location_label.trim() !== "") {
    rows.push({ label: "Location", value: context.location_label.trim() });
  }
  if (context.build_order_number != null) {
    rows.push({ label: "Build order", value: peekField(context.build_order_number) });
  }
  if (context.mv_number != null) {
    rows.push({ label: "MV", value: peekField(context.mv_number) });
  }
  if (context.mv_status != null) {
    rows.push({ label: "Status", value: peekField(context.mv_status) });
  }
  if (context.planned_quantity != null) {
    rows.push({
      label: "Planned",
      value: <span className="tabular-nums">{context.planned_quantity.toLocaleString()}</span>,
    });
  }
  if (context.quantity_complete != null) {
    rows.push({
      label: "Complete",
      value: <span className="tabular-nums">{context.quantity_complete.toLocaleString()}</span>,
    });
  }
  if (context.planned_quantity != null && context.quantity_complete != null) {
    rows.push({
      label: "Remaining",
      value: (
        <span className="tabular-nums">
          {Math.max(0, context.planned_quantity - context.quantity_complete).toLocaleString()}
        </span>
      ),
    });
  }
  if (context.fail_count != null) {
    rows.push({
      label: "Fails",
      value: <span className="tabular-nums">{context.fail_count.toLocaleString()}</span>,
    });
  }
  if (context.avg_line_harvest_delta != null) {
    rows.push({ label: "Avg delta", value: context.avg_line_harvest_delta.toFixed(2) });
  }
  if (context.received_for_shipment != null) {
    rows.push({
      label: "Received for shipment",
      value: (
        <span className="tabular-nums">
          {context.received_for_shipment.received.toLocaleString()}/
          {context.received_for_shipment.required.toLocaleString()}
        </span>
      ),
    });
  }
  if (rows.length === 0) {
    return <span className="text-slate-500">No work order details.</span>;
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function SkuQuantitiesPeekHoverHintBody({
  lines,
  emptyMessage = "No line-level detail.",
}: {
  lines: readonly { sku: string; quantity: number }[];
  emptyMessage?: ReactNode;
}): ReactNode {
  if (lines.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return <HoverHintDefinitionGrid rows={skuQuantityPeekRows(lines)} />;
}

export function SalesOrderListPeekHoverHintBody({
  contexts,
  emptyMessage = "No linked sales order context.",
}: {
  contexts: readonly CalendarSalesOrderPeekContext[];
  emptyMessage?: ReactNode;
}): ReactNode {
  if (contexts.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return (
    <div className="max-w-[22rem] space-y-2 whitespace-normal text-left">
      {contexts.map((context, index) => {
        const rowKey =
          context.salesOrderNumber?.trim() ??
          `${String(context.clientName ?? "")}-${String(context.endUserName ?? "")}-${String(index)}`;
        return (
          <div
            key={rowKey}
            className={index > 0 ? "space-y-1 border-t border-slate-100 pt-2" : "space-y-1"}
          >
            {context.salesOrderNumber != null && context.salesOrderNumber.trim() !== "" ? (
              <div className="text-xs font-semibold text-slate-900">{context.salesOrderNumber.trim()}</div>
            ) : null}
            <SalesOrderPeekHoverHintBody
              context={{
                client_name: context.clientName,
                end_user_name: context.endUserName,
                sgs_ship_by_date: context.sgsShipByDate,
              }}
              emptyMessage="No details."
            />
          </div>
        );
      })}
    </div>
  );
}
